import { Component, input, output, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ComponentSize } from '../types';

export interface TreeTableColumn {
  field: string;
  header: string;
  width?: string;
}

export interface TreeTableNode {
  id: string;
  data: { [key: string]: any };
  children?: TreeTableNode[];
  expanded?: boolean;
  selected?: boolean;
}

@Component({
  selector: 'app-tree-table',
  imports: [CommonModule],
  template: `
    <div class="overflow-x-auto">
      <table [class]="tableClasses">
        <thead>
          <tr>
            <th class="w-12"></th>
            @for (col of columns(); track col.field) {
              <th [class]="getHeaderClasses()" [style.width]="col.width">
                {{ col.header }}
              </th>
            }
          </tr>
        </thead>
        <tbody>
          @for (node of nodes(); track node.id) {
            <tr [class]="getRowClasses(node)">
              <td>
                @if (node.children && node.children.length > 0) {
                  <button
                    type="button"
                    (click)="toggleNode(node)"
                    class="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                    <svg 
                      class="w-4 h-4 transition-transform duration-200"
                      [class.rotate-90]="node.expanded"
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                }
              </td>
              @for (col of columns(); track col.field) {
                <td [class]="getCellClasses()">
                  {{ node.data[col.field] }}
                </td>
              }
            </tr>
            @if (node.children && node.children.length > 0 && node.expanded) {
              @for (child of node.children; track child.id) {
                <tr [class]="getRowClasses(child)">
                  <td class="pl-8">
                    @if (child.children && child.children.length > 0) {
                      <button
                        type="button"
                        (click)="toggleNode(child)"
                        class="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                        <svg 
                          class="w-4 h-4 transition-transform duration-200"
                          [class.rotate-90]="child.expanded"
                          fill="none" 
                          stroke="currentColor" 
                          viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    }
                  </td>
                  @for (col of columns(); track col.field) {
                    <td [class]="getCellClasses()">
                      {{ child.data[col.field] }}
                    </td>
                  }
                </tr>
              }
            }
          }
        </tbody>
      </table>
    </div>
  `,
  styles: []
})
export class TreeTableComponent {
  columns = input<TreeTableColumn[]>([]);
  nodes = input<TreeTableNode[]>([]);
  size = input<ComponentSize>('md');
  striped = input<boolean>(false);
  hoverable = input<boolean>(true);

  nodeToggleEvent = output<TreeTableNode>();

  toggleNode(node: TreeTableNode): void {
    node.expanded = !node.expanded;
    this.nodeToggleEvent.emit(node);
  }

  get tableClasses(): string {
    return 'min-w-full divide-y divide-gray-200 dark:divide-gray-700';
  }

  getHeaderClasses(): string {
    const sizeMap = {
      xs: 'px-2 py-1 text-xs',
      sm: 'px-3 py-2 text-sm',
      md: 'px-4 py-3 text-base',
      lg: 'px-5 py-4 text-lg',
      xl: 'px-6 py-5 text-xl'
    };
    return `px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-800 ${sizeMap[this.size()]}`;
  }

  getRowClasses(node: TreeTableNode): string {
    const classes = ['bg-white', 'dark:bg-gray-800'];
    if (this.striped()) {
      classes.push('even:bg-gray-50', 'dark:even:bg-gray-800/50');
    }
    if (this.hoverable()) {
      classes.push('hover:bg-gray-50', 'dark:hover:bg-gray-700');
    }
    if (node.selected) {
      classes.push('bg-blue-50', 'dark:bg-blue-900/20');
    }
    return classes.join(' ');
  }

  getCellClasses(): string {
    const sizeMap = {
      xs: 'px-2 py-1 text-xs',
      sm: 'px-3 py-2 text-sm',
      md: 'px-4 py-3 text-base',
      lg: 'px-5 py-4 text-lg',
      xl: 'px-6 py-5 text-xl'
    };
    return `px-4 py-3 whitespace-nowrap text-gray-900 dark:text-gray-100 ${sizeMap[this.size()]}`;
  }
}

